import { useMemo } from 'react'

/**
 * The data bridge script tag and the #page-data div live INSIDE the #app div
 * (Liquid renders them there). When React calls createRoot(app).render(...)
 * it clears #app's children, which removes those nodes from the DOM.
 *
 * So we must capture the payload at module load time, before createRoot runs.
 * main.tsx → App → usePageData.ts all evaluate synchronously before
 * createRoot(root).render(<App />) executes, so the DOM still contains the
 * original server-rendered nodes at that point.
 */

function readPageData(): unknown {
  if (typeof document === 'undefined') return null

  // Primary: JSON payload in <script type="application/json" id="page-data-json">.
  // We use a script tag because platformOS's `escape` filter does not escape
  // double quotes inside HTML attribute values, breaking the attribute-based
  // payload for any non-empty JSON.
  const scriptEl = document.getElementById('page-data-json')
  if (scriptEl?.textContent) {
    try {
      return JSON.parse(scriptEl.textContent)
    } catch {
      /* fall through to attribute fallback */
    }
  }

  // Legacy fallback: data-payload attribute on #page-data.
  const el = document.getElementById('page-data')
  if (el) {
    try {
      const raw = el.getAttribute('data-payload') ?? '{}'
      return JSON.parse(raw)
    } catch {
      /* fall through */
    }
  }
  return null
}

function readPageName(): string {
  if (typeof document === 'undefined') return 'home'
  const el = document.getElementById('page-data')
  return el?.getAttribute('data-page') ?? 'home'
}

// Captured synchronously at module evaluation, before React mounts.
const CACHED_PAGE_DATA = readPageData()
const CACHED_PAGE_NAME = readPageName()

export function usePageData<T = unknown>(): T | null {
  return useMemo(() => (CACHED_PAGE_DATA as T) ?? null, [])
}

export function usePageName(): string {
  return CACHED_PAGE_NAME
}
